import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Group,
  Breadcrumbs,
  Divider,
  Box,
  Space,
  Anchor,
  Stack,
  Button,
  Loader,
  ActionIcon,
  LoadingOverlay,
} from '@mantine/core';
import { Container } from 'reactstrap';
import { DndProvider } from 'react-dnd-latest';
import { HTML5Backend } from 'react-dnd-html5-backend-latest';
import { IconEye } from '@tabler/icons';
import { QuestionnaireContext } from '../../Service/QuestionnaireContextV2';
import LinkText from '../../Atoms/LinkText';
import SkeletonItems, { QuestionBundleSkeleton } from '../../Atoms/SkeletonItems';
import QuestionBundle from '../../Molecules/Settings/QuestionnaireTemplate/QuestionBundle';
import { EventRichInput, RichInput } from '../../Organismns/tiptap/TiptapEditorInput';
import DocumentBubbleMenu from '../../Organismns/tiptap/toolbars/BubbleMenu';
import { TiptapS } from '../../Service/TiptapS';
import QuestionnaireAlert from '../../Atoms/QuestionnaireAlert';
import { WindowContext } from '../../Service/Context/WindowContext';
import { TestS } from '../../TestS';
import { PlaceholderS } from '../../Service/PlaceholderS';
import { useEventScope } from '../../Service/hooks/useEventScope';
import QuestionnaireActions from './QuestionnaireActions';
import { FooterGap } from '../Navigation/phone/InjectableFooter';

const QuestionnaireEditor = () => {
  const { loading, insertingBundle, questionnaire, patchQuestionnaire, bundleHandlers, questionHandlers } =
    useContext(QuestionnaireContext);
  const navigate = useNavigate();
  const { isEventScope } = useEventScope();
  const { sm } = useContext(WindowContext);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const { insertBundle } = bundleHandlers;
  const { id: questionnaireId, headRow, endRow, questionBundles, label, eventId } = questionnaire ?? {};
  const emptyBundles = questionBundles?.length === 0;

  const showPreview = async () => {
    if (questionnaireId) {
      setLoadingPreview(true);
      const allResolved = await PlaceholderS.getUnresolvedPlaceholders(questionnaireId, 'questionnaires');
      setLoadingPreview(false);
      if (allResolved) {
        navigate('preview');
      }
    }
  };

  return (
    <Container p={0} pos="relative">
      {!sm && (
        <>
          <Group position="apart" sx={{ alignItems: 'center' }}>
            <Breadcrumbs>
              <Anchor onClick={() => navigate(`/events/${eventId}/documents`)}>Dateien</Anchor>
              <Anchor sx={{ pointerEvents: 'none' }}>
                <b>{loading ? 'Laden ...' : label || 'Nicht gefunden '}</b>
              </Anchor>
            </Breadcrumbs>
            <Group>
              {!sm && (
                <Button
                  size="xs"
                  disabled={loadingPreview}
                  leftIcon={loadingPreview ? <Loader size="xs" color="white" /> : <IconEye size={20} />}
                  onClick={showPreview}
                  data-testid={TestS.Navigate.Documents.TO_PREVIEW}
                >
                  Vorschau
                </Button>
              )}
              {sm && (
                <ActionIcon color="blue" variant="filled">
                  <IconEye
                    color="white"
                    size={20}
                    onClick={showPreview}
                    data-testid={TestS.Navigate.Documents.TO_PREVIEW}
                  />
                </ActionIcon>
              )}
              {questionnaireId && <QuestionnaireActions id={questionnaireId} showPreview={showPreview} />}
            </Group>
          </Group>
          <Divider my="xs" />
        </>
      )}
      <QuestionnaireAlert />
      {headRow !== undefined && !isEventScope && (
        <RichInput
          label="Kopfzeile"
          value={headRow}
          patchField={(htmlString) => patchQuestionnaire({ headRow: htmlString })}
          menu={<DocumentBubbleMenu />}
          styles={TiptapS.TextInputStyles}
        />
      )}
      {headRow !== undefined && isEventScope && (
        <EventRichInput
          label="Kopfzeile"
          value={headRow}
          patchField={(htmlString) => patchQuestionnaire({ headRow: htmlString })}
          menu={<DocumentBubbleMenu />}
          styles={TiptapS.TextInputStyles}
        />
      )}
      {loading && <SkeletonItems height={100} />}
      <Box pos="relative">
        <LoadingOverlay visible={insertingBundle} overlayBlur={2} loaderProps={{ size: 'xl' }} />
        {!loading && emptyBundles && (
          <>
            <QuestionBundleSkeleton />
            <LinkText mt={5} mb="xl" ml="sm" onClick={() => insertBundle()}>
              + Abschnitt hinzufügen
            </LinkText>
          </>
        )}
        {!emptyBundles && (
          <DndProvider backend={HTML5Backend}>
            {questionBundles && (
              <Stack my="xl">
                {questionBundles.map((qb, index) => (
                  <QuestionBundle
                    index={index}
                    key={qb.id}
                    bundle={qb}
                    bundleHandlers={bundleHandlers}
                    questionHandlers={questionHandlers}
                  />
                ))}
              </Stack>
            )}
          </DndProvider>
        )}
      </Box>
      {endRow !== undefined && !isEventScope && (
        <RichInput
          label="Fußzeile"
          value={endRow}
          patchField={(htmlString) => patchQuestionnaire({ endRow: htmlString })}
          menu={<DocumentBubbleMenu />}
          styles={{ minHeight: 150 }}
        />
      )}
      {endRow !== undefined && isEventScope && (
        <EventRichInput
          label="Fußzeile"
          value={endRow}
          patchField={(htmlString) => patchQuestionnaire({ endRow: htmlString })}
          menu={<DocumentBubbleMenu />}
          styles={{ minHeight: 150 }}
        />
      )}
      <Space h="xl" />
      <FooterGap />
    </Container>
  );
};

export default QuestionnaireEditor;
